import type { FeatureName } from "replo-utils/lib/featureFlags";

import { initializePosthogIfNeeded } from "@v2/features/infra/analytics/init";
import { shouldLogAnalytics } from "@v2/features/infra/analytics/tracking";
import { isUnitTest } from "@v2/utils/env";
import posthog from "posthog-js";
import { featureFlags } from "replo-utils/lib/featureFlags";
import { isNotNullish } from "replo-utils/lib/misc";
import { booleanAsStringDefaultSchema } from "schemas/utils";

export function isFeatureEnabled(featureName: FeatureName) {
  initializePosthogIfNeeded();

  // Note (Sebas, 2023-04-05): In case we are in a unit test, we don't want to
  // use any feature flags, so we return false.
  if (isUnitTest) {
    return false;
  }

  // Note (Noah, 2024-11-14): Disable all announcements in e2e tests
  if (
    window.localStorage.getItem("REPLO_IS_E2E_TEST") &&
    featureName.startsWith("announcement_")
  ) {
    return false;
  }

  const featureFlagOverrides:
    | { [key in FeatureName]?: boolean }
    // @ts-expect-error
    | undefined = window.E2E_FEATURE_FLAG_OVERRIDES;
  if (featureFlagOverrides) {
    const override = featureFlagOverrides[featureName];
    if (isNotNullish(override)) {
      return override;
    }
  }

  // NOTE (Yuxin, 2026-07-28): Beta invite auto-opens a blocking modal that intercepts clicks and breaks unrelated e2e flows; default it off in e2e (a test can still opt in via the overrides above).
  if (
    window.localStorage.getItem("REPLO_IS_E2E_TEST") &&
    featureName === "agent-launch-modal"
  ) {
    return false;
  }

  if (!shouldLogAnalytics()) {
    const localStorageValue = localStorage.getItem(
      `replo.localDev.featureFlags.${featureName}`,
    );
    if (localStorageValue) {
      const value = booleanAsStringDefaultSchema.safeParse(localStorageValue);
      if (value.success) {
        return value.data;
      }
      return false;
    }
  }

  return Boolean(posthog?.isFeatureEnabled(featureName, { send_event: false }));
}

// posthog's `override` replaces the entire override map, so every flag we want
// to keep must be re-asserted on each toggle. The static `featureFlags` list
// omits dynamically-named flags (`announcement_*`), so we also merge posthog's
// current overrides — otherwise toggling one flag silently reverts a sibling's
// override.
function buildFeatureFlagOverrides(
  featureName: FeatureName,
  value: boolean,
): Record<string, boolean | string> {
  // posthog types get_property as `any`; guard for an object before spreading.
  const existingOverrides = posthog.get_property("$override_feature_flags");
  return {
    ...Object.fromEntries(
      featureFlags.map((item) => [item.value, isFeatureEnabled(item.value)]),
    ),
    ...(existingOverrides && typeof existingOverrides === "object"
      ? existingOverrides
      : {}),
    [featureName]: value,
  };
}

export function enableFeatureFlag(featureName: FeatureName) {
  if (!shouldLogAnalytics()) {
    localStorage.setItem(
      `replo.localDev.featureFlags.${featureName}`,
      JSON.stringify(true),
    );
    return;
  }

  initializePosthogIfNeeded();
  return posthog.featureFlags.override(
    buildFeatureFlagOverrides(featureName, true),
  );
}

export function disableFeatureFlag(featureName: FeatureName) {
  if (!shouldLogAnalytics()) {
    localStorage.setItem(
      `replo.localDev.featureFlags.${featureName}`,
      JSON.stringify(false),
    );
    return;
  }

  initializePosthogIfNeeded();
  return posthog.featureFlags.override(
    buildFeatureFlagOverrides(featureName, false),
  );
}

export function clearFeatureFlagOverrides() {
  if (!shouldLogAnalytics()) {
    for (const featureName of featureFlags) {
      localStorage.removeItem(`replo.localDev.featureFlags.${featureName}`);
    }
    return;
  }

  initializePosthogIfNeeded();
  return posthog.featureFlags.override(false);
}
